import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Chip,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Spinner,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tabs,
  useDisclosure
} from '@nextui-org/react';
import { apiFetch } from '../lib/api';
import { applySort } from '../lib/sort';
import { CATALOG, PACKS } from '../data/widgets-catalog';
import { thumbFor } from '../data/widgetThumbs';
import { MARK_FIELDS, WidgetMarks, liveMark, markFieldsFor, markKeyFor, markStatus, markStatusText, today } from '../lib/widgetMarks';

// Galería de widgets: el catálogo se genera desde la app Electron en cada
// dev/build (scripts/sync-widgets-catalog.mjs), las miniaturas salen de los
// assets de la app (data/widgetThumbs.js) y los contadores de uso de la tabla
// `configs` de D1 (GET /api/backoffice/widgets/summary).
//
// Etiquetas NEW y «Mejorado»: qué widget es nuevo o se ha mejorado se pone aquí, en la ficha
// de cada uno (GET/PUT /api/backoffice/widgets/marks), y la app lo recibe en el feed del
// Inicio. Lo que la app trae escrito es la reserva de un widget del que aquí no se ha dicho nada.

const PAGE_LIMIT = 200;

// El servidor está saturado un rato después de cada despliegue («D1 DB is overloaded») y a
// veces rechaza una lectura o un guardado: se vuelve a intentar solo, con una pausa cada vez
// más larga. Guardar una etiqueta se puede repetir sin miedo: es poner el mismo valor.
const BUSY_RE = /overloaded|queued for too long|\bbusy\b|^50[234]\b|Failed to fetch|NetworkError/i;
const BUSY_WAITS_MS = [1200, 2500, 5000];
const isBusyError = (e) => BUSY_RE.test(String((e && e.message) || e || ''));
const BUSY_TEXT = 'El servidor está saturado ahora mismo (pasa un rato después de cada actualización). Vuelve a intentarlo en un minuto.';
async function withBusyRetry(run, onRetry) {
  for (let attempt = 0; ; attempt++) {
    try {
      return await run();
    } catch (e) {
      if (attempt >= BUSY_WAITS_MS.length || !isBusyError(e)) throw e;
      onRetry?.(attempt + 1);
      await new Promise((resolve) => setTimeout(resolve, BUSY_WAITS_MS[attempt]));
    }
  }
}

const CATEGORY_LABELS = { rankings: 'Rankings', free: 'Free', premium: 'Premium' };
const PACK_LABELS = {
  tikcontrol: 'TikControl',
  gladiador: 'Gladiador',
  magia: 'Bosque Encantado',
  basico: 'Chroma',
  mundial: 'World Cup 2026',
  'community-fest': 'Community Fest 2026',
  atlantis: 'Atlantis',
  candy: 'Candy',
  halloween: 'Halloween',
  muertos: 'Día de los Muertos',
  random: 'Pack Random'
};

const SORT_OPTIONS = [
  ['catalog', 'Orden del catalogo'],
  ['title', 'Titulo A-Z'],
  ['installs', 'Mas usados'],
  ['lastUpdated', 'Config mas reciente']
];

// Tabla plana (mismo estilo que Users).
const FLAT_TABLE_CLASSNAMES = {
  th: 'rounded-none bg-transparent border-b border-default-300 text-[11px] uppercase tracking-wider text-default-500 first:rounded-none last:rounded-none',
  tr: 'border-b border-default-200/70 odd:bg-default-100/40 even:bg-transparent transition-colors hover:bg-default-200/40',
  td: 'first:rounded-none last:rounded-none'
};

function formatBytes(n) {
  if (!n) return '0';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function formatTs(n) {
  if (!n) return '—';
  const d = new Date(n);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString('es-ES');
}

function formatCount(n) {
  return Number(n || 0).toLocaleString('es-ES');
}

// Claves de D1 que cuentan como "uso" de un widget del catálogo. Las
// animaciones se guardan por tipo (anim-x2), no por pack: todos los packs de
// un mismo tipo comparten contador.
function usageKeysFor(widget) {
  const keys = [widget.key, widget.key.replace(/_/g, '-'), widget.key.replace(/-/g, '_')];
  if (widget.animation && widget.animType) keys.push(`anim-${widget.animType}`);
  return [...new Set(keys)];
}

function initialsFor(title) {
  return String(title || '?')
    .split(/[\s-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join('');
}

// Vista previa: miniatura real si existe; si no, un fondo con las iniciales.
function WidgetPreview({ widget }) {
  const [broken, setBroken] = useState(false);
  const src = thumbFor(widget);
  if (src && !broken) {
    return (
      <img
        src={src}
        alt=""
        loading="lazy"
        onError={() => setBroken(true)}
        className="h-full w-full object-cover"
      />
    );
  }
  const tone = widget.animation
    ? 'from-secondary-500/40 to-secondary-900/60'
    : widget.category === 'rankings'
      ? 'from-primary-500/40 to-primary-900/60'
      : widget.tier === 'premium'
        ? 'from-warning-500/40 to-warning-900/60'
        : 'from-success-500/40 to-success-900/60';
  return (
    <div className={`flex h-full w-full items-center justify-center bg-gradient-to-br ${tone}`}>
      <span className="text-3xl font-semibold tracking-wide text-white/80">{initialsFor(widget.title)}</span>
    </div>
  );
}

// La etiqueta que la tarjeta del widget lleva hoy en la app (los colores de la galería).
function MarkChip({ mark }) {
  if (!mark) return null;
  const look = mark === 'new'
    ? { text: 'NEW', className: 'bg-[#ff0050] text-white' }
    : { text: 'MEJORADO', className: 'bg-gradient-to-r from-[#00d5c8] to-[#3ddc84] text-[#04221f]' };
  return <span className={`inline-flex h-5 items-center rounded-md px-1.5 text-[10px] font-bold tracking-wide ${look.className}`}>{look.text}</span>;
}

// El servidor no ha dado las etiquetas (aún no las conoce, o no responde): se dice aquí y los
// botones quedan apagados, para no guardar a ciegas encima de lo que haya.
function MarksUnavailable({ error }) {
  if (!error) return null;
  return (
    <p className="rounded-md border border-warning-300/60 bg-warning-50/10 p-2 text-xs text-warning-600" role="status">
      Ahora mismo no se pueden leer las etiquetas del servidor ({error}). Hasta que responda no se pueden cambiar.
    </p>
  );
}

// Una etiqueta de un widget: desde qué día cuenta. Cada botón guarda al momento.
function MarkField({ widget, field, config, busy, onChange }) {
  const meta = MARK_FIELDS[field];
  const key = markKeyFor(widget);
  const status = markStatus(config, key, field);
  const value = status.source === 'backoffice' && status.since ? status.since : '';
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-default-200 bg-content2/40 p-3">
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold">{meta.title}</span>
        {status.live && <MarkChip mark={field === 'newSince' ? 'new' : 'improved'} />}
      </div>
      <label className="flex flex-col gap-1 text-xs text-default-500">
        {meta.since}
        <input
          type="date"
          aria-label={`${meta.since} (${widget.title})`}
          className="rounded-lg border border-default-300 bg-content2 p-2 text-sm text-foreground disabled:opacity-50"
          value={value}
          disabled={busy}
          onChange={(e) => { if (WidgetMarks.validDay(e.target.value)) onChange({ key, [field]: e.target.value }); }}
        />
      </label>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="flat" color="primary" isDisabled={busy} onPress={() => onChange({ key, [field]: today() })}>
          Desde hoy
        </Button>
        <Button size="sm" variant="flat" isDisabled={busy || (status.source === 'backoffice' && status.since === null)} onPress={() => onChange({ key, [field]: null })}>
          Sin etiqueta
        </Button>
        <Button size="sm" variant="light" isDisabled={busy || status.source === 'app'} onPress={() => onChange({ key, reset: [field] })}>
          Como viene en la app
        </Button>
      </div>
      <span className="text-xs text-default-500" role="status">{markStatusText(status)}</span>
    </div>
  );
}

// Las etiquetas de la ficha de un widget (o, en una animación, la NEW de su pack).
function WidgetMarksEditor({ widget, config, busy, error, onChange }) {
  const fields = markFieldsFor(widget);
  if (!fields.length) return null;
  return (
    <section className="flex flex-col gap-3 rounded-lg border border-primary-300/60 bg-content1 p-4">
      <div>
        <h3 className="text-base font-semibold">Etiquetas en la app</h3>
        <p className="text-xs text-default-500">
          {widget.animation
            ? `Las animaciones van por packs: esta etiqueta es la del pack «${PACK_LABELS[widget.pack] || widget.pack}» entero.`
            : 'El widget sale en «Novedades» o «Mejorados» de la galería y del Editor, con su marco y su etiqueta, mientras dure. Si lleva las dos, la tarjeta enseña NEW.'}
          {' '}Llega a las apps abiertas en un minuto, sin publicar versión.
        </p>
      </div>
      <MarksUnavailable error={error} />
      <div className={`grid gap-3 ${fields.length > 1 ? 'md:grid-cols-2' : ''}`}>
        {fields.map((field) => (
          <MarkField key={field} widget={widget} field={field} config={config} busy={busy || !!error} onChange={onChange} />
        ))}
      </div>
    </section>
  );
}

// Cuántos días dura cada etiqueta, para todos los widgets.
function MarkDaysCard({ config, busy, error, onSave }) {
  const days = WidgetMarks.sanitize(config).days;
  const [draft, setDraft] = useState(days);
  useEffect(() => { setDraft({ newDays: days.newDays, improvedDays: days.improvedDays }); }, [days.newDays, days.improvedDays]);
  const valid = WidgetMarks.validDays(draft.newDays) && WidgetMarks.validDays(draft.improvedDays);
  const changed = draft.newDays !== days.newDays || draft.improvedDays !== days.improvedDays;
  const input = (key, label) => (
    <label className="flex flex-col gap-1 text-xs text-default-500">
      {label}
      <input
        type="number"
        min="1"
        max="365"
        className="w-40 rounded-lg border border-default-300 bg-content2 p-2 text-sm text-foreground disabled:opacity-50"
        value={draft[key]}
        disabled={busy || !!error}
        onChange={(e) => setDraft({ ...draft, [key]: e.target.value === '' ? '' : Number(e.target.value) })}
      />
    </label>
  );
  return (
    <section className="flex flex-col gap-3 rounded-lg border border-default-200 bg-content1 p-4">
      <div>
        <h2 className="text-base font-semibold">Etiquetas NEW y «Mejorado»</h2>
        <p className="text-xs text-default-500">
          Abre un widget para decir desde qué día es nuevo o está mejorado. Pasados estos días desde su fecha, la etiqueta se quita sola en las apps.
          De un widget del que aquí no se ha dicho nada, la app usa la etiqueta que trae escrita.
        </p>
      </div>
      <MarksUnavailable error={error} />
      <div className="flex flex-wrap items-end gap-3">
        {input('newDays', 'Días con la etiqueta NEW')}
        {input('improvedDays', 'Días con la etiqueta «Mejorado»')}
        <Button size="sm" color="primary" isDisabled={busy || !!error || !valid || !changed} onPress={() => onSave({ days: draft })}>
          Guardar duración
        </Button>
      </div>
    </section>
  );
}

function WidgetCard({ widget, summaryLoading, onOpenDetail, onOpenConfigs }) {
  const usageKey = widget.animation ? `anim-${widget.animType}` : widget.key;
  return (
    <div className="group flex flex-col overflow-hidden rounded-lg border border-default-200 bg-content1 transition hover:border-default-400">
      <button
        type="button"
        onClick={onOpenDetail}
        className="relative aspect-video w-full overflow-hidden bg-content2 text-left"
        aria-label={`Ver ${widget.title}`}
      >
        <WidgetPreview widget={widget} />
        <div className="absolute left-2 top-2 flex flex-wrap gap-1">
          {widget.tier === 'premium'
            ? <Chip size="sm" variant="solid" color="warning" className="h-5 text-[10px]">Premium</Chip>
            : <Chip size="sm" variant="solid" color="success" className="h-5 text-[10px]">Free</Chip>}
          <MarkChip mark={widget.mark} />
          {widget.devOnly && <Chip size="sm" variant="solid" color="secondary" className="h-5 text-[10px]">dev</Chip>}
          {widget.disabled && <Chip size="sm" variant="solid" color="danger" className="h-5 text-[10px]">desactivado</Chip>}
        </div>
      </button>
      <div className="flex flex-1 flex-col gap-1.5 p-3">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold">{widget.title}</div>
          <code className="block truncate text-[11px] text-default-400">{widget.key}</code>
        </div>
        <div className="flex flex-wrap gap-1 text-[11px] text-default-500">
          <span className="rounded bg-default-100 px-1.5 py-0.5">{CATEGORY_LABELS[widget.category] || widget.category}</span>
          {widget.v2 && <span className="rounded bg-default-100 px-1.5 py-0.5">v2</span>}
          {widget.animation && (
            <>
              <span className="rounded bg-secondary-100 px-1.5 py-0.5 text-secondary-700">{widget.animType}</span>
              <span className="rounded bg-default-100 px-1.5 py-0.5">{PACK_LABELS[widget.pack] || widget.pack}</span>
            </>
          )}
        </div>
        <div className="mt-auto flex items-end justify-between gap-2 pt-2">
          <div className="text-xs text-default-500">
            {summaryLoading ? (
              <Spinner size="sm" />
            ) : (
              <>
                <span className={`font-semibold ${widget.installs > 0 ? 'text-foreground' : 'text-default-400'}`}>
                  {formatCount(widget.installs)}
                </span>{' '}
                configs{widget.animation ? ` (tipo ${widget.animType})` : ''}
                {widget.lastUpdated ? <div className="text-[11px] text-default-400">ultima {formatTs(widget.lastUpdated)}</div> : null}
              </>
            )}
          </div>
          <Button
            size="sm"
            variant="flat"
            className="h-7 min-w-0 px-2 text-xs"
            isDisabled={!widget.installs}
            onPress={() => onOpenConfigs(usageKey)}
          >
            Configs
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function Widgets() {
  const [view, setView] = useState('gallery');

  // Summary (counts por widget, para cruzar con catálogo)
  const [summary, setSummary] = useState(null); // Map<widget, {count, last_updated}>
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(null);

  // Gallery filters
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState('all');
  const [packFilter, setPackFilter] = useState('all');
  const [gallerySort, setGallerySort] = useState('catalog');
  const [markFilter, setMarkFilter] = useState('all');

  // Etiquetas NEW y «Mejorado» (lo guardado; null = aquí no se ha dicho nada)
  const [marks, setMarks] = useState(null);
  const [marksBusy, setMarksBusy] = useState(false);
  const [marksError, setMarksError] = useState(null);
  const [configsSort, setConfigsSort] = useState({
    column: 'updated_at',
    direction: 'descending'
  });

  // Configs list (drill-down)
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [uidFilter, setUidFilter] = useState('');
  const [widgetFilter, setWidgetFilter] = useState('');
  const [cfgLoading, setCfgLoading] = useState(false);
  const [cfgError, setCfgError] = useState(null);
  const [toast, setToast] = useState(null);

  const detail = useDisclosure();
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  async function loadSummary() {
    setSummaryLoading(true);
    setSummaryError(null);
    try {
      const res = await apiFetch('/api/backoffice/widgets/summary');
      const map = new Map();
      for (const r of res.rows || []) {
        map.set(r.widget, { count: r.count, last_updated: r.last_updated });
      }
      setSummary(map);
    } catch (e) {
      setSummaryError(e.message);
    } finally {
      setSummaryLoading(false);
    }
  }

  async function loadMarks() {
    try {
      const res = await withBusyRetry(() => apiFetch('/api/backoffice/widgets/marks', { cache: 'no-store' }));
      setMarks(res.config || null);
      setMarksError(null);
    } catch (e) {
      // Sin error rojo: el resto de la página funciona igual. Se dice junto a las etiquetas.
      setMarksError(isBusyError(e) ? 'el servidor está saturado; prueba «Refrescar» en un minuto' : (e.message || 'sin respuesta'));
    }
  }

  // Un cambio cada vez (un widget o la duración): el servidor lo aplica sobre lo guardado.
  async function saveMarks(change) {
    setMarksBusy(true);
    try {
      const res = await withBusyRetry(
        () => apiFetch('/api/backoffice/widgets/marks', { method: 'PUT', body: change }),
        (attempt) => setToast({ type: 'ok', msg: `El servidor está ocupado: reintentando (${attempt} de ${BUSY_WAITS_MS.length})…` })
      );
      setMarks(res.config || null);
      setToast({ type: 'ok', msg: 'Etiquetas guardadas. Las apps abiertas lo ven en un minuto.' });
    } catch (e) {
      setToast({ type: 'error', msg: isBusyError(e) ? `No se ha guardado. ${BUSY_TEXT}` : `No se pudo guardar la etiqueta: ${e.message}` });
    } finally {
      setMarksBusy(false);
    }
  }

  async function loadConfigs(nextOffset = 0, overrides = {}) {
    setCfgLoading(true);
    setCfgError(null);
    try {
      const uid = (overrides.uid ?? uidFilter).trim();
      const widget = (overrides.widget ?? widgetFilter).trim();
      const params = new URLSearchParams({
        limit: String(PAGE_LIMIT),
        offset: String(nextOffset)
      });
      if (uid) params.set('uid', uid);
      if (widget) params.set('widget', widget);
      const res = await apiFetch(`/api/backoffice/widgets/configs?${params}`);
      setRows(res.rows || []);
      setTotal(res.total || 0);
      setOffset(nextOffset);
    } catch (e) {
      setCfgError(e.message);
    } finally {
      setCfgLoading(false);
    }
  }

  useEffect(() => {
    loadSummary();
    loadMarks();
  }, []);

  useEffect(() => {
    if (view === 'configs' && rows.length === 0 && !cfgLoading) {
      loadConfigs(0);
    }
  }, [view]);

  // Catálogo + uso real (D1). Para animaciones el contador es por tipo.
  const enriched = useMemo(() => {
    return CATALOG.map((w, index) => {
      let installs = 0;
      let lastUpdated = 0;
      for (const key of usageKeysFor(w)) {
        const stats = summary?.get(key);
        if (!stats) continue;
        installs += Number(stats.count || 0);
        lastUpdated = Math.max(lastUpdated, Number(stats.last_updated || 0));
      }
      return { ...w, order: index, installs, lastUpdated, mark: liveMark(marks, w) };
    });
  }, [summary, marks]);

  const filteredGallery = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const base = enriched.filter((w) => {
      if (catFilter !== 'all' && w.category !== catFilter) return false;
      if (tierFilter === 'free' && w.tier !== 'free') return false;
      if (tierFilter === 'premium' && (w.tier !== 'premium' || w.animation)) return false;
      if (tierFilter === 'animation' && !w.animation) return false;
      if (packFilter !== 'all' && w.pack !== packFilter) return false;
      if (markFilter !== 'all' && w.mark !== markFilter) return false;
      if (!needle) return true;
      return (
        w.key.toLowerCase().includes(needle) ||
        w.title.toLowerCase().includes(needle) ||
        (w.animType || '').toLowerCase().includes(needle) ||
        (w.pack || '').toLowerCase().includes(needle) ||
        (PACK_LABELS[w.pack] || '').toLowerCase().includes(needle)
      );
    });
    const sorters = {
      catalog: { column: 'order', direction: 'ascending' },
      title: { column: 'title', direction: 'ascending' },
      installs: { column: 'installs', direction: 'descending' },
      lastUpdated: { column: 'lastUpdated', direction: 'descending' }
    };
    return applySort(base, sorters[gallerySort] || sorters.catalog, {
      order: (w) => w.order,
      title: (w) => w.title.toLowerCase(),
      installs: (w) => w.installs,
      lastUpdated: (w) => w.lastUpdated
    });
  }, [enriched, search, catFilter, tierFilter, packFilter, markFilter, gallerySort]);

  const galleryCounts = useMemo(() => {
    const acc = { all: CATALOG.length, rankings: 0, free: 0, premium: 0 };
    for (const w of CATALOG) acc[w.category] = (acc[w.category] || 0) + 1;
    return acc;
  }, []);

  // Cuántas tarjetas llevan hoy cada etiqueta en la app.
  const markCounts = useMemo(() => {
    const acc = { new: 0, improved: 0 };
    for (const w of enriched) if (w.mark) acc[w.mark]++;
    return acc;
  }, [enriched]);

  const tierCounts = useMemo(() => {
    const acc = { free: 0, premium: 0, animation: 0 };
    for (const w of CATALOG) {
      if (w.animation) acc.animation++;
      else if (w.tier === 'free') acc.free++;
      else acc.premium++;
    }
    return acc;
  }, []);

  async function openDetailConfig(row) {
    setSelected({ uid: row.uid, widget: row.widget, row: null });
    detail.onOpen();
    setDetailLoading(true);
    try {
      const res = await apiFetch(
        `/api/backoffice/widgets/configs/${encodeURIComponent(row.uid)}/${encodeURIComponent(row.widget)}`
      );
      setSelected({ uid: row.uid, widget: row.widget, row: res.row });
    } catch (e) {
      setToast({ type: 'error', msg: e.message });
      detail.onClose();
    } finally {
      setDetailLoading(false);
    }
  }

  function openGalleryDetail(w) {
    setSelected({ uid: null, widget: w.key, catalog: w });
    detail.onOpen();
  }

  function openConfigsFor(widgetKey) {
    setView('configs');
    setWidgetFilter(widgetKey);
    setUidFilter('');
    loadConfigs(0, { widget: widgetKey, uid: '' });
  }

  async function handleDelete(row) {
    if (
      !confirm(
        `¿Borrar la config "${row.widget}" del usuario ${row.uid}? Esta acción no se puede deshacer.`
      )
    )
      return;
    try {
      await apiFetch(
        `/api/backoffice/widgets/configs/${encodeURIComponent(row.uid)}/${encodeURIComponent(row.widget)}`,
        { method: 'DELETE' }
      );
      setToast({ type: 'ok', msg: 'Config eliminada.' });
      loadConfigs(offset);
      loadSummary();
    } catch (e) {
      setToast({ type: 'error', msg: e.message });
    }
  }

  const selectedThumb = selected?.catalog ? thumbFor(selected.catalog) : null;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Widgets</h1>
          <p className="text-sm text-default-500">
            {CATALOG.length} widgets del catalogo de la app, con su uso real (configs guardadas en D1).
          </p>
        </div>
        <Button
          size="sm"
          variant="flat"
          onPress={() => {
            loadSummary();
            loadMarks();
            if (view === 'configs') loadConfigs(offset);
          }}
          isLoading={summaryLoading || cfgLoading}
        >
          Refrescar
        </Button>
      </div>

      <Tabs
        selectedKey={view}
        onSelectionChange={(k) => setView(String(k))}
        aria-label="Vista"
      >
        <Tab key="gallery" title="Galeria" />
        <Tab key="configs" title="Configs por usuario" />
      </Tabs>

      {toast && (
        <div
          className={`rounded-md border p-3 text-sm ${
            toast.type === 'ok'
              ? 'border-success-200 bg-success-50 text-success-700'
              : 'border-danger-200 bg-danger-50 text-danger-700'
          }`}
        >
          <div className="flex items-center justify-between gap-3">
            <span>{toast.msg}</span>
            <button className="text-xs underline" onClick={() => setToast(null)}>
              cerrar
            </button>
          </div>
        </div>
      )}

      {view === 'gallery' && (
        <>
          {summaryError && (
            <div className="rounded-md border border-warning-200 bg-warning-50 p-3 text-sm text-warning-700">
              No se pudieron cargar los contadores de uso: {summaryError}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <Input
              placeholder="Buscar por titulo, key, pack o animacion…"
              value={search}
              onValueChange={setSearch}
              isClearable
              size="sm"
              className="max-w-sm"
            />
            <Tabs
              size="sm"
              selectedKey={catFilter}
              onSelectionChange={(k) => setCatFilter(String(k))}
              aria-label="Categoria"
            >
              <Tab key="all" title={`Todos (${galleryCounts.all})`} />
              <Tab key="rankings" title={`Rankings (${galleryCounts.rankings})`} />
              <Tab key="free" title={`Free (${galleryCounts.free})`} />
              <Tab key="premium" title={`Premium (${galleryCounts.premium})`} />
            </Tabs>
            <Tabs
              size="sm"
              selectedKey={tierFilter}
              onSelectionChange={(k) => setTierFilter(String(k))}
              aria-label="Tipo"
            >
              <Tab key="all" title="Todos" />
              <Tab key="free" title={`Free (${tierCounts.free})`} />
              <Tab key="premium" title={`Premium (${tierCounts.premium})`} />
              <Tab key="animation" title={`Animaciones (${tierCounts.animation})`} />
            </Tabs>
            <Tabs
              size="sm"
              selectedKey={markFilter}
              onSelectionChange={(k) => setMarkFilter(String(k))}
              aria-label="Etiqueta"
            >
              <Tab key="all" title="Con y sin etiqueta" />
              <Tab key="new" title={`NEW (${markCounts.new})`} />
              <Tab key="improved" title={`Mejorado (${markCounts.improved})`} />
            </Tabs>
            <Select
              aria-label="Pack"
              size="sm"
              className="w-48"
              disallowEmptySelection
              selectedKeys={[packFilter]}
              onSelectionChange={(keys) => setPackFilter(String(Array.from(keys)[0] || 'all'))}
            >
              {[['all', 'Todos los packs'], ...PACKS.map((pack) => [pack, PACK_LABELS[pack] || pack])].map(([key, label]) => (
                <SelectItem key={key}>{label}</SelectItem>
              ))}
            </Select>
            <Select
              aria-label="Orden"
              size="sm"
              className="w-48"
              disallowEmptySelection
              selectedKeys={[gallerySort]}
              onSelectionChange={(keys) => setGallerySort(String(Array.from(keys)[0] || 'catalog'))}
            >
              {SORT_OPTIONS.map(([key, label]) => <SelectItem key={key}>{label}</SelectItem>)}
            </Select>
            <span className="ml-auto text-xs text-default-400">{filteredGallery.length} widgets</span>
          </div>

          {filteredGallery.length === 0 ? (
            <div className="border-t border-default-200 p-8 text-center text-sm text-default-400">Sin coincidencias.</div>
          ) : (
            <div className="grid grid-cols-2 gap-3 border-t border-default-200 pt-4 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
              {filteredGallery.map((w) => (
                <WidgetCard
                  key={w.key}
                  widget={w}
                  summaryLoading={summaryLoading}
                  onOpenDetail={() => openGalleryDetail(w)}
                  onOpenConfigs={openConfigsFor}
                />
              ))}
            </div>
          )}

          <MarkDaysCard config={marks} busy={marksBusy} error={marksError} onSave={saveMarks} />

          <p className="text-xs text-default-400">
            Catalogo generado desde la app en cada build (<code>npm run catalog:sync</code> para forzarlo).
            Miniaturas: las de la galeria de la app; los widgets sin miniatura muestran sus iniciales.
          </p>
        </>
      )}

      {view === 'configs' && (
        <>
          {cfgError && (
            <div className="rounded-md border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">
              {cfgError}
            </div>
          )}
          <div className="flex flex-wrap items-end gap-3">
            <Input
              label="UID exacto"
              size="sm"
              value={uidFilter}
              onValueChange={setUidFilter}
              className="max-w-xs"
              isClearable
            />
            <Input
              label="Widget (contiene)"
              size="sm"
              value={widgetFilter}
              onValueChange={setWidgetFilter}
              className="max-w-xs"
              isClearable
            />
            <Button
              onPress={() => loadConfigs(0)}
              color="primary"
              size="sm"
              isLoading={cfgLoading}
            >
              Aplicar filtro
            </Button>
            <span className="ml-auto text-sm text-default-500">
              {cfgLoading
                ? 'Cargando…'
                : total === 0
                ? '0 de 0'
                : `${offset + 1}-${Math.min(offset + rows.length, total)} de ${total}`}
            </span>
          </div>

          {cfgLoading ? (
            <div className="flex justify-center p-8">
              <Spinner />
            </div>
          ) : (
            <div className="overflow-x-auto border-t border-default-200">
              <Table
                aria-label="Configs de widgets"
                removeWrapper
                classNames={FLAT_TABLE_CLASSNAMES}
                sortDescriptor={configsSort}
                onSortChange={setConfigsSort}
              >
                <TableHeader>
                  <TableColumn key="uid" allowsSorting>UID</TableColumn>
                  <TableColumn key="widget" allowsSorting>Widget</TableColumn>
                  <TableColumn key="config_size" allowsSorting>config</TableColumn>
                  <TableColumn key="state_size" allowsSorting>state</TableColumn>
                  <TableColumn key="data_size" allowsSorting>data</TableColumn>
                  <TableColumn key="updated_at" allowsSorting>Actualizado</TableColumn>
                  <TableColumn align="end">Acciones</TableColumn>
                </TableHeader>
                <TableBody emptyContent="No hay configs con estos filtros.">
                  {applySort(rows, configsSort, {
                    uid: (r) => (r.uid || '').toLowerCase(),
                    widget: (r) => (r.widget || '').toLowerCase(),
                    config_size: (r) => r.config_size || 0,
                    state_size: (r) => r.state_size || 0,
                    data_size: (r) => r.data_size || 0,
                    updated_at: (r) => r.updated_at || 0
                  }).map((r) => (
                    <TableRow key={`${r.uid}::${r.widget}`}>
                      <TableCell>
                        <code className="text-xs">{r.uid}</code>
                      </TableCell>
                      <TableCell>{r.widget}</TableCell>
                      <TableCell>{formatBytes(r.config_size)}</TableCell>
                      <TableCell>{formatBytes(r.state_size)}</TableCell>
                      <TableCell>{formatBytes(r.data_size)}</TableCell>
                      <TableCell>{formatTs(r.updated_at)}</TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-2">
                          <Button size="sm" variant="flat" onPress={() => openDetailConfig(r)}>
                            Ver
                          </Button>
                          <Button
                            size="sm"
                            variant="flat"
                            color="danger"
                            onPress={() => handleDelete(r)}
                          >
                            Borrar
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="flat"
              isDisabled={offset === 0 || cfgLoading}
              onPress={() => loadConfigs(Math.max(0, offset - PAGE_LIMIT))}
            >
              ← Anterior
            </Button>
            <Button
              size="sm"
              variant="flat"
              isDisabled={offset + rows.length >= total || cfgLoading}
              onPress={() => loadConfigs(offset + PAGE_LIMIT)}
            >
              Siguiente →
            </Button>
          </div>
        </>
      )}

      <Modal
        isOpen={detail.isOpen}
        onClose={detail.onClose}
        size="3xl"
        scrollBehavior="inside"
      >
        <ModalContent>
          <ModalHeader>
            <div className="flex flex-col">
              <span>
                {selected?.catalog?.title || selected?.widget}
                {selected?.uid && (
                  <>
                    {' '}
                    <span className="text-default-400">·</span>{' '}
                    <code className="text-xs">{selected.uid}</code>
                  </>
                )}
              </span>
              {selected?.catalog && <code className="text-xs font-normal text-default-400">{selected.catalog.key}</code>}
            </div>
          </ModalHeader>
          <ModalBody>
            {detailLoading ? (
              <div className="flex justify-center p-8">
                <Spinner />
              </div>
            ) : (
              <>
                {selected?.catalog && (
                  <WidgetMarksEditor widget={selected.catalog} config={marks} busy={marksBusy} error={marksError} onChange={saveMarks} />
                )}
                {selectedThumb && (
                  <img src={selectedThumb} alt="" className="max-h-[320px] w-full rounded-lg object-contain bg-content2" />
                )}
                <pre className="max-h-[60vh] overflow-auto rounded bg-default-100 p-3 text-xs">
                  {selected?.row
                    ? JSON.stringify(selected.row, null, 2)
                    : selected?.catalog
                    ? JSON.stringify(selected.catalog, null, 2)
                    : ''}
                </pre>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="flat" onPress={detail.onClose}>
              Cerrar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
}
